import { usePrivy, useWallets, WalletWithMetadata } from '@privy-io/react-auth';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { getMaxLeverage } from '@/features/trade/order/utils/math'; // eslint-disable-line import/no-cycle
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import useCapitalStatus from '@/lib/api/get-capital-status';
import { useAccountStore } from '@/store/use-account-store';
import { getMarketStore } from '@/store/use-markets-store';
import { AssetType, CapitalStatus } from '@/types';

const FUNDED_ACCOUNT_MAX_LEVERAGE = 5;

export const getFundedMaxLeverage = (symbol: string): number => {
  const { fundedInitMarginRatio } =
    getMarketStore(symbol).getState().marketSpec;

  if (fundedInitMarginRatio && fundedInitMarginRatio > 0n) {
    return (
      Number(getMaxLeverage(fundedInitMarginRatio)?.decimal) ||
      FUNDED_ACCOUNT_MAX_LEVERAGE
    );
  }

  return FUNDED_ACCOUNT_MAX_LEVERAGE;
};

const ALLOWED_CRYPTO_SYMBOLS = ['BTC-PERP', 'ETH-PERP', 'SOL-PERP'];

export default function useFundedAccount() {
  const { user } = usePrivy();
  const { wallets } = useWallets();

  const addressByLowerCase = useMemo(() => {
    if (!user?.linkedAccounts) {
      return {};
    }

    return Object.fromEntries(
      user.linkedAccounts
        .filter((acc) => acc.type === 'wallet')
        .map((acc) => [acc.address.toLowerCase(), acc.address]),
    );
  }, [user?.linkedAccounts]);

  const capitalStatusByAddress = useAccountStore(
    useShallow((state) => state.capitalStatusByAddress),
  );
  const capitalStatus = useAccountStore((state) =>
    state.getCapitalStatus(state.address),
  );

  const fundedAccountWallet = useMemo(() => {
    return wallets.find((wallet) => {
      const capitalStatus = useAccountStore
        .getState()
        .getCapitalStatus(wallet.address);
      return (
        capitalStatus === CapitalStatus.ACTIVE ||
        capitalStatus === CapitalStatus.CREATED ||
        capitalStatus === CapitalStatus.PRE_EVAL ||
        capitalStatus === CapitalStatus.PENDING_PAYMENT ||
        capitalStatus === CapitalStatus.PAID
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallets, capitalStatusByAddress]);

  const addresses = useMemo(() => {
    if (!user?.linkedAccounts) return [];
    return (
      user.linkedAccounts.filter(
        (acc) => acc.type === 'wallet',
      ) as WalletWithMetadata[]
    ).map((acc) => acc.address.toLowerCase());
  }, [user?.linkedAccounts]);

  const { data: capitalStatusData, isLoading: isStatusLoading } =
    useCapitalStatus({ addresses });

  const activePlan = fundedAccountWallet
    ? capitalStatusData?.[fundedAccountWallet.address.toLowerCase()]
        ?.active_capital_plan
    : undefined;

  const evalAddress = capitalStatusData
    ? Object.entries(capitalStatusData).find(
        ([_, data]) => data.capital_status === CapitalStatus.ACTIVE,
      )?.[0]
    : undefined;

  const fundedAddress = capitalStatusData
    ? Object.entries(capitalStatusData).find(
        ([_, data]) => data.capital_status === CapitalStatus.FUNDED,
      )?.[0]
    : undefined;

  const rawEvalAddress = evalAddress
    ? addressByLowerCase[evalAddress]
    : undefined;
  const rawFundedAddress = fundedAddress
    ? addressByLowerCase[fundedAddress]
    : undefined;

  const hasEval = !!rawEvalAddress;
  const hasFunded = !!rawFundedAddress;

  const isEval = getIsStatusEval(capitalStatus);

  const isFundedOrEval = useMemo(() => {
    switch (capitalStatus) {
      case CapitalStatus.UNKNOWN:
      case CapitalStatus.NOT_ELIGIBLE:
        return false;
      default:
        return true;
    }
  }, [capitalStatus]);

  return {
    capitalStatusData,
    activeCapitalPlan: activePlan,
    hasEval,
    evalAddress: rawEvalAddress,
    hasFunded,
    fundedAddress: rawFundedAddress,
    isEval,
    isFundedOrEval,
    fundedAccountWallet,
    isStatusLoading,
  };
}

export const isMarketAllowed = (symbol: string) => {
  if (ALLOWED_CRYPTO_SYMBOLS.includes(symbol)) return true;
  const { marketSpec } = getMarketStore(symbol).getState();
  return marketSpec.asset !== AssetType.CRYPTO;
};
